import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Palette, Type, Copy, Check, Star, Users, MessageSquare, Megaphone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useAdmin } from './AdminContext';
import { toast } from 'sonner';

export const AdminBrandKitTab = () => {
  const { adminKey } = useAdmin();
  const [kit, setKit] = useState(null);
  const [copied, setCopied] = useState('');

  useEffect(() => {
    const fetchKit = async () => {
      try {
        const res = await fetch('/api/admin/brand-kit', { headers: { 'X-Admin-Key': adminKey } });
        if (res.ok) setKit(await res.json());
      } catch { }
    };
    fetchKit();
  }, []);

  const copyText = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopied(label);
    toast.success(`Copied: ${label}`);
    setTimeout(() => setCopied(''), 2000);
  };

  const CopyBtn = ({ text, label }) => (
    <button onClick={() => copyText(text, label)} className="p-1 rounded hover:bg-white/10 transition" title="Copy">
      {copied === label ? <Check size={12} className="text-green-400" /> : <Copy size={12} className="text-gray-500" />}
    </button>
  );

  if (!kit) return <div className="h-32 bg-card animate-pulse rounded-xl" />;

  return (
    <div className="space-y-6" data-testid="brand-kit-tab">
      <div>
        <h2 className="text-xl font-bold">Brand & Marketing Kit</h2>
        <p className="text-sm text-muted-foreground">Everything you need for consistent Twin Tube branding</p>
      </div>

      {/* Logo & Identity */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
        <Card className="border-border bg-card overflow-hidden">
          <div className="p-6 bg-gradient-to-r from-purple-900/40 via-card to-blue-900/40">
            <div className="text-center">
              <h1 className="text-5xl font-black tracking-tight mb-2">
                <span className="text-purple-400">TWIN</span>
                <span className="text-blue-400 ml-2">TUBE</span>
              </h1>
              <p className="text-sm text-purple-300/80">{kit.secondary_tagline}</p>
              <p className="text-xs text-gray-400 uppercase tracking-widest mt-1">{kit.tagline}</p>
            </div>
          </div>
          <CardContent className="pt-4">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">Brand Name</p>
                  <p className="font-bold">{kit.brand_name}</p>
                </div>
                <CopyBtn text={kit.brand_name} label="brand_name" />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">Primary Tagline</p>
                  <p className="text-sm">{kit.tagline}</p>
                </div>
                <CopyBtn text={kit.tagline} label="tagline" />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">Secondary Tagline</p>
                  <p className="text-sm">{kit.secondary_tagline}</p>
                </div>
                <CopyBtn text={kit.secondary_tagline} label="secondary_tagline" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Mission Statement</p>
                <p className="text-sm text-gray-300 leading-relaxed">{kit.mission}</p>
                <CopyBtn text={kit.mission} label="mission" />
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Color Palette */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
        <Card className="border-border bg-card">
          <CardHeader>
            <CardTitle className="text-sm flex items-center gap-2"><Palette size={16} className="text-purple-400" /> Color Palette</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              {Object.entries(kit.colors).map(([key, color]) => (
                <button
                  key={key}
                  onClick={() => copyText(color.hex, key)}
                  className="text-left group"
                >
                  <div
                    className="w-full h-16 rounded-lg border border-border group-hover:ring-2 ring-white/20 transition"
                    style={{ backgroundColor: color.hex }}
                  />
                  <p className="text-xs font-semibold mt-1.5">{color.name}</p>
                  <p className="text-[10px] text-muted-foreground font-mono">{color.hex}</p>
                  <p className="text-[9px] text-gray-500 mt-0.5">{color.usage}</p>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Typography */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
        <Card className="border-border bg-card">
          <CardHeader>
            <CardTitle className="text-sm flex items-center gap-2"><Type size={16} className="text-purple-400" /> Typography</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-muted-foreground">Heading Font</p>
                <p className="text-sm font-semibold">{kit.typography.heading_font}</p>
                <p className="text-xs text-gray-500">Weights: {kit.typography.heading_weights}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Body Font</p>
                <p className="text-sm">{kit.typography.body_font}</p>
                <p className="text-xs text-gray-500">Weights: {kit.typography.body_weights}</p>
              </div>
            </div>
            <div className="p-4 rounded-lg bg-black/30 border border-border space-y-2">
              <p className="text-3xl font-extrabold">Heading Example</p>
              <p className="text-lg font-semibold text-purple-400">Subheading in Primary</p>
              <p className="text-sm text-gray-300">Body text example — supporting local Twin Cities creativity since 2026.</p>
              <p className="text-xs text-muted-foreground">Caption / small text style</p>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Voice & Tone */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
        <Card className="border-border bg-card">
          <CardHeader>
            <CardTitle className="text-sm flex items-center gap-2"><MessageSquare size={16} className="text-purple-400" /> Voice & Tone</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm mb-3"><span className="font-semibold text-purple-300">Personality:</span> {kit.voice_tone.personality}</p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs font-semibold text-green-400 mb-2">Do</p>
                <ul className="space-y-1">
                  {kit.voice_tone.do.map((item, i) => (
                    <li key={i} className="text-xs text-gray-300 flex items-start gap-1.5">
                      <span className="text-green-400 mt-0.5">+</span> {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <p className="text-xs font-semibold text-red-400 mb-2">Don't</p>
                <ul className="space-y-1">
                  {kit.voice_tone.dont.map((item, i) => (
                    <li key={i} className="text-xs text-gray-300 flex items-start gap-1.5">
                      <span className="text-red-400 mt-0.5">-</span> {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Social Templates */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}>
        <Card className="border-border bg-card">
          <CardHeader>
            <CardTitle className="text-sm flex items-center gap-2"><Megaphone size={16} className="text-purple-400" /> Social Media Templates</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {Object.entries(kit.social_templates).map(([key, template]) => (
              <div key={key} className="p-3 rounded-lg bg-black/30 border border-border">
                <div className="flex items-center justify-between mb-1">
                  <p className="text-xs font-semibold text-purple-300 capitalize">{key.replace(/_/g, ' ')}</p>
                  <CopyBtn text={template} label={`template_${key}`} />
                </div>
                <p className="text-sm text-gray-300 italic">"{template}"</p>
              </div>
            ))}
          </CardContent>
        </Card>
      </motion.div>

      {/* Badges */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
        <Card className="border-border bg-card">
          <CardHeader>
            <CardTitle className="text-sm flex items-center gap-2"><Star size={16} className="text-purple-400" /> Profile Badges</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {kit.badges.map(badge => (
                <div key={badge.key} className="flex items-start gap-3 p-3 rounded-lg border border-border">
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase shrink-0" style={{ background: `${badge.color}20`, color: badge.color, border: `1px solid ${badge.color}40` }}>
                    {badge.label}
                  </span>
                  <p className="text-xs text-gray-400">{badge.description}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Target Audience */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }}>
        <Card className="border-border bg-card">
          <CardHeader>
            <CardTitle className="text-sm flex items-center gap-2"><Users size={16} className="text-purple-400" /> Target Audience</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {kit.target_audience.map((audience, i) => (
                <div key={i} className="flex items-center gap-2 p-2 rounded-lg bg-purple-500/5 border border-purple-500/10">
                  <span className="w-5 h-5 rounded-full bg-purple-500/20 flex items-center justify-center text-[10px] font-bold text-purple-400 shrink-0">{i + 1}</span>
                  <span className="text-xs text-gray-300">{audience}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};
